import { mfLogoBaseUrl } from '../../../env'
import RenderHTML from '../../ui-components/RenderHTML'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { FolioDetail, FolioSource, WithdrawalLearnMoreReason } from '../../../types/mf-execution.types'
import { isInsufficientBalanceFolio } from '../../../utils/mf-execution/withdrawUtils'


const copyConfig: Record<WithdrawalLearnMoreReason, string> = {
  [WithdrawalLearnMoreReason.LOCKIN]: 'Folio units are under lock-in. Withdrawals can only be placed once the lock-in period is over.',
  [WithdrawalLearnMoreReason.PLEDGED]: 'Folio units are pledged. Withdrawals can only be placed once the pledge is removed.',
  [WithdrawalLearnMoreReason.DEMAT]: 'Folio is in demat form. Withdrawals can only be placed through your broker.',
  [WithdrawalLearnMoreReason.CAN_NOT_WITHDRAW]: 'Folio units are not available for withdrawal. Withdrawals can only be placed once your units are settled and available.',
  [WithdrawalLearnMoreReason.GENERIC]: 'Folio units are not available for withdrawal. Withdrawals can only be placed once your units are settled and available.'
}

export const FolioDetails = ({
  folioDetails,
  isBorderTop = true
}: {
  folioDetails: FolioDetail
  isBorderTop?: boolean
}) => {
  const isInsufficientBalance = isInsufficientBalanceFolio(folioDetails)
  const isDisabled = folioDetails?.isUnavailable || isInsufficientBalance

  const getDisabledReason = () => {
    if (isInsufficientBalance) return 'Not enough balance to withdraw'
    return copyConfig?.[folioDetails?.unavailabilityReasons?.[0]] ?? copyConfig?.[WithdrawalLearnMoreReason.GENERIC]
  }

  return (
    <div className={`flex flex-col`}>
      {isBorderTop && <div className='border-t border-[#e2e8f0] w-full' />}
      <div
        className={`flex items-center justify-between pt-4 transition-colors duration-200 px-5 ${
          isDisabled ? '' : 'cursor-pointer hover:bg-[#f8fafc]'
        }`}
      >
        <div className='flex gap-3 flex-1'>
          <div className='w-[40px] h-[40px] rounded-[12px]'>
            <img
              src={`${mfLogoBaseUrl}${folioDetails?.logoUrl}`}
              alt='Folio Logo'
              className='w-full h-full object-contain'
              onError={(e) => {
                const target = e?.target as HTMLImageElement
                target.style.display = 'none'
              }}
            />
          </div>
          <div className={`flex flex-col gap-[2px] flex-1`}>
            <div className={`text-[14px] font-[500] text-[#232323] leading-[130%]`}>{folioDetails?.folioName}</div>
            <div className={`text-[13px] font-[400] text-[#6A6E6E] leading-[150%]`}>
              <span className='font-[500]'>{roundOffAndFormatNumberWithCurrency(folioDetails?.folioAmount, true, 'floor')}</span>{' '}
              available
            </div>
            <div className='flex gap-2 items-center pt-2'>
              {folioDetails?.source === FolioSource.EXTERNAL && (
                <div className='text-[#6A6E6E] font-[400] text-[13px] rounded-[8px] w-max bg-[#F0F4F7] px-2 pt-[3px] pb-[5px]'>
                  External
                </div>
              )}
              <div
                style={{ background: 'linear-gradient(90deg, #F0F4F7 0%, rgba(240, 244, 247, 0.00) 100%)' }}
                className='text-[#6A6E6E] font-[400] text-[13px] rounded-[8px] pl-[10px] py-[4px] pr-10 w-max'
              >
                Folio #{folioDetails?.folioNumber}
              </div>
            </div>
          </div>
        </div>
        <div className={`text-[#94a3b8] w-5 text-[20px] font-light `}>
          {!isDisabled && <RenderHTML innerHTMLText={RightChevron} />}
        </div>
      </div>
      {(folioDetails?.unavailabilityReasons?.length > 0 || isInsufficientBalance) && (
        <div className='pr-5 pl-[74px] pt-2 text-[12px] font-[400] text-black leading-[150%]'>
          {getDisabledReason()}
        </div>
      )}
    </div>
  )
}

const RightChevron = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
  <path d="M1.0957 13.0001L6.7693 7.32648C6.94958 7.14624 6.94958 6.85401 6.7693 6.67377L1.0957 1.00012" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
